/**
 * How each document type is drawn.
 *
 * ── A hue NAME and an icon, never a colour ───────────────────────────────
 * The design prototype carries `col:"#EF4444"` on every one of these, which is
 * exactly the literal the Single-Palette Rule exists to keep out of the
 * codebase. `tone` is a hue token's name, resolved through `TINT_CLASS` and
 * `ON_TINT_CLASS`, so it follows the theme and colour-blind mode like
 * everything else. `null` means the neutral surface, which is a real choice —
 * a policy is not a colour.
 *
 * ── This is presentation, so it lives beside the screens ─────────────────
 * Deliberately NOT in `features/documents/api.js`: that file is the wire seam
 * and knows about `DocumentType`, not about icons. Keyed by the server's enum
 * values, so a type added server-side shows as a plain glyph rather than
 * crashing — see the fallback in `lookOf`.
 *
 * ── There is no `ext` any more ───────────────────────────────────────────
 * The demo's table carried one per type: DWG for drawings and floor plans, DOC
 * for procedures, PDF for the rest. `FilePurpose.document` accepts
 * `application/pdf` ALONE, so five of those eight were a promise the uploader
 * cannot keep — a reader shown "DWG" would reasonably try to file one. The
 * thumbnail shows the glyph and the revision number instead, both of which are
 * true. Recorded in BACKEND-GAPS.md.
 */
export const DOC_TYPE_LOOK = {
  oem_manual: { tone: 'red', icon: 'doc' },
  procedure: { tone: 'forge', icon: 'doc' },
  drawing: { tone: 'green', icon: 'layers' },
  datasheet: { tone: 'violet', icon: 'doc' },
  certificate: { tone: 'amber', icon: 'shield' },
  policy: { tone: null, icon: 'shield' },
  report: { tone: 'watch', icon: 'chart' },
  floor_plan: { tone: 'green', icon: 'map' },
}

/** The look for a type, or a neutral one for a type this build does not know. */
export const lookOf = (documentType) =>
  DOC_TYPE_LOOK[documentType] ?? { tone: null, icon: 'doc' }

/**
 * Which hue a revision's ingestion status takes.
 *
 * `uploaded` is deliberately neutral rather than amber: the bytes are stored
 * and the engine has simply not read them yet, which is the ordinary state of
 * a document filed a minute ago and not a problem anybody should act on.
 */
export const REVISION_STATUS_TONE = {
  uploaded: null,
  processed: 'green',
  failed: 'red',
}
